import React, { useCallback, useEffect, useState } from "react";
import BackLink from "../components/BackLink";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { ReactComponent as IconPhone } from "../assets/icon-phone.svg";
import JourneyProgress from "../components/JourneyProgress";
import { useGetUnitListQuery } from "../features/rapidstor/rapidstorApiSlice";
import { useLoaderData, useNavigate, Link } from "react-router-dom";
import Location from "../features/rapidstor/entities/Location";
import classes from "./UnitListing.module.scss";
import { setUnitData } from "../store/bookingSlice";
import store from "../store";
import QuoteForm from "../components/UnitListing/QuoteForm";
import { useAppliedFilters } from "../hooks/useAppliedFilters";
import UnitType from "../features/rapidstor/entities/UnitType";
import DescriptorT from "../features/rapidstor/entities/Descriptor";
import ReactSimplyCarousel from "../components/UnitListing/Carousel";
import Countdown from "../components/Countdown";
import StandardSizeSlide from "../components/UnitListing/StandardSizeSlide";
import { useStandardSizes } from "../hooks/useStandardSizes";
import { StandardSizeMatch } from "../features/rapidstor/entities/StandardSize";

interface SelectedUnitPerSize {
  [sizeLabel: string]: { unitTypeId: string; descriptorId: string };
}

function UnitListing() {
  const location = useLoaderData() as Location;
  const navigate = useNavigate();

  const [carouselIndex, setCarouselIndex] = useState<number>(0);
  const [quoteModalOpen, setQuoteModalOpen] = useState<boolean>(false);
  const [defaultSelected, setDefaultSelected] = useState<number | undefined>(undefined);
  const [selectedUnits, setSelectedUnits] = useState<SelectedUnitPerSize>({});
  const [unitAvailable, setUnitAvailable] = useState<boolean>(false);

  const {
    data: unitList,
    isLoading,
    error,
    refetch,
  } = useGetUnitListQuery(location.locationCode);

  const { filters } = useAppliedFilters();
  const { slides, isLoading: isLoadingStandardSizes } = useStandardSizes(
    unitList,
    location,
    filters
  );
  const isSizeAvailable = useCallback(
    (index: number) => {
      const match = slides[index];
      if (!match) return false;
      const selection = selectedUnits[match.definition.label];
      if (selection) {
        const selectedUnit = match.unitTypes.find((u) => u._id === selection.unitTypeId);
        return (selectedUnit?.totalAvailable ?? 0) > 0;
      }
      return match.available;
    },
    [slides, selectedUnits]
  );

  useEffect(() => {
    if (!slides.length) return;

    // If no questionnaire filters exist at all, default to first slide
    const hasFilters = Object.keys(filters).length > 0;
    if (!hasFilters) {
      setCarouselIndex(0);
      setDefaultSelected(0);
      return;
    }

    // Score each slide across all questionnaire filters, mirroring old scoreDescriptor logic
    const scoreSlide = (match: StandardSizeMatch): number => {
      return Math.max(
        ...match.unitTypes.map((unitType) => {
          const perFilter = Object.entries(filters).map(([filter, value]) => {
            switch (filter) {
              case "dcArea.min":
                return unitType.area >= (value as number)
                  ? 2
                  : unitType.area - (value as number);
              case "dcLength.min":
                return unitType.length >= (value as number)
                  ? 2
                  : unitType.length - (value as number);
              case "bInside":
                return unitType.inside === value ? 1 : -10;
              case "sTypeName.re": {
                const pattern = value as string;
                if (pattern.charAt(0) === "!") {
                  return !new RegExp(pattern.substring(1), "i").test(unitType.typeName)
                    ? 1
                    : -10;
                }
                return new RegExp(pattern, "i").test(unitType.typeName) ? 1 : -10;
              }
              default:
                return 1;
            }
          });
          if (perFilter.length === 0) return 0;
          return perFilter.slice(1).reduce<number>((acc, cur) => {
            if (cur < 0 && acc < 0) return cur + acc;
            else if (cur > 0 && acc > 0) return cur + acc;
            else return Math.min(cur, acc);
          }, perFilter[0]);
        })
      );
    };
    const scored = slides
      .map((match, index): [number, number] => [index, scoreSlide(match)])
      .sort((a, b) => {
        // Smallest positives first (closest match), then largest negatives
        if (a[1] > 0 && b[1] > 0) return a[1] - b[1];
        return b[1] - a[1];
      });
    const firstAvailable = scored.find(
      ([idx, score]) => slides[idx]?.available && score > 0
    );
    if (firstAvailable) {
      setCarouselIndex(firstAvailable[0]);
      setDefaultSelected(firstAvailable[0]);
    } else {
      setCarouselIndex(scored[0][0]);
      setDefaultSelected(scored[0][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides, filters]);

  function getActiveSlideData(): {
    unitType: UnitType;
    descriptor: DescriptorT;
  } | null {
    const match = slides[carouselIndex];
    if (!match?.descriptor) return null;

    const selection = selectedUnits[match.definition.label];
    if (selection) {
      const ut = match.unitTypes.find((u) => u._id === selection.unitTypeId);
      const desc = unitList?.descriptors.find((d) => d._id === selection.descriptorId);
      if (ut && desc) return { unitType: ut, descriptor: desc };
    }

    const bestGroup = match.bestGroup;
    const bestUnit = bestGroup ? match.unitGroups[bestGroup] : null;
    if (bestUnit && match.descriptor) {
      return { unitType: bestUnit, descriptor: match.descriptor };
    }
    return null;
  }

  function goToBookingForm(
    unitType: UnitType,
    descriptor: DescriptorT,
    reservation: boolean
  ) {
    store.dispatch(setUnitData({ location, unitType, descriptor, reservation }));
    navigate(
      reservation
        ? `/locations/${location.slug}/reserve`
        : `/locations/${location.slug}/book`
    );
  }

  function onBookUnitClickedFromSlide(unitTypeOverride?: UnitType) {
    const data = getActiveSlideData();
    if (!data) return;
    const effectiveUnitType = unitTypeOverride ?? data.unitType;
    goToBookingForm(effectiveUnitType, data.descriptor, false);
  }

  function onBookUnitClicked() {
    onBookUnitClickedFromSlide();
  }

  const onEmailQuoteClicked = useCallback(() => {
    const data = getActiveSlideData();
    if (data) {
      store.dispatch(
        setUnitData({
          location,
          unitType: data.unitType,
          descriptor: data.descriptor,
          reservation: false,
        })
      );
      setQuoteModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carouselIndex, slides, location, selectedUnits]);

  const onUnitTypeSelected = useCallback(
    (descriptor: DescriptorT, unitType: UnitType, slideIndex: number) => {
      const match = slides[slideIndex];
      if (!match) return;

      setSelectedUnits((prev) => ({
        ...prev,
        [match.definition.label]: {
          unitTypeId: unitType._id,
          descriptorId: descriptor._id,
        },
      }));
    },
    [slides]
  );

  useEffect(() => {
    setUnitAvailable(isSizeAvailable(carouselIndex));
  }, [carouselIndex, isSizeAvailable, selectedUnits]);

  const shouldShowSlide = (index: number) => {
    return Math.abs(carouselIndex - index) <= 2;
  };

  const ready = !isLoading && slides.length > 0 && defaultSelected !== undefined;
  const itemsToShow = Math.min(5, slides.length || 1);

  return (
    <>
      <Container className="phone-container listing-container">
        <Container>
          <BackLink desktop />
        </Container>
        <Container>
          <Row>
            <Col xs={12}>
              <JourneyProgress mobile className="mt-2" />
              <Countdown minutes={20} seconds={0} />
              {!location ? null : isLoading || defaultSelected === undefined ? (
                "Unit list is loading"
              ) : !slides || error ? (
                <Alert className="mt-4" variant="secondary">
                  Sorry, we were unable to load the list of units.{" "}
                  <div className="mt-2">
                    <Button onClick={refetch} className="ms-2" variant="secondary">
                      Retry
                    </Button>
                  </div>
                </Alert>
              ) : (
                <>
                  <Container
                    className={classes.results_header}
                    style={{ maxWidth: "900px" }}
                  >
                    <h3>Your Results</h3>
                  </Container>
                  <Container style={{ maxWidth: "900px" }}>
                    <ReactSimplyCarousel
                      containerProps={{
                        style: {
                          width: "100%",
                          userSelect: "none",
                          marginLeft: "-12px",
                          marginTop: "20px",
                        },
                      }}
                      swipeTreshold={60}
                      activeSlideIndex={carouselIndex}
                      activeSlideProps={{
                        className: "active-slide",
                        style: { borderRadius: "4px" },
                      }}
                      onRequestChange={setCarouselIndex}
                      forwardBtnProps={{
                        style: {
                          marginTop: "20px",
                          opacity: 0.5,
                          border: 0,
                          background: "transparent",
                          color: "rgb(0, 0, 0, 1)",
                          alignSelf: "center",
                        },
                      }}
                      backwardBtnProps={{
                        style: {
                          marginTop: "20px",
                          opacity: 0.5,
                          border: 0,
                          background: "transparent",
                          color: "rgb(0, 0, 0, 1)",
                          alignSelf: "center",
                        },
                      }}
                      dotsNav={{
                        show: true,
                        itemBtnProps: {
                          style: {
                            height: 8,
                            width: 8,
                            padding: 0,
                            borderRadius: "50%",
                            border: 0,
                            margin: "0 2px",
                            background: "rgba(0, 0, 0, 0.2)",
                          },
                        },
                        activeItemBtnProps: {
                          style: {
                            height: 8,
                            width: 8,
                            padding: 0,
                            borderRadius: "50%",
                            border: 0,
                            margin: "0 2px",
                          },
                        },
                      }}
                      itemsToShow={itemsToShow}
                      speed={300}
                      centerMode
                      infinite={slides.length > itemsToShow}
                    >
                      {slides.map((match: StandardSizeMatch, index: number) => (
                        <div
                          key={match.definition.label}
                          className={classes.descriptor_wrapper}
                          style={{ opacity: shouldShowSlide(index) ? 1 : 0 }}
                        >
                          <StandardSizeSlide
                            onBookUnitClicked={onBookUnitClickedFromSlide}
                            onEmailQuoteClicked={onEmailQuoteClicked}
                            onUnitTypeSelected={onUnitTypeSelected}
                            slideIndex={index}
                            location={location}
                            match={match}
                            recommended={index === defaultSelected}
                          />
                        </div>
                      ))}
                    </ReactSimplyCarousel>
                  </Container>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </Container>

      {location && ready && !error ? (
        <>
          <div className={`${classes.bottomPanel} border-top`}>
            <Container className="phone-container">
              <Row>
                <Col xs={6}>
                  <Button
                    variant="outline-primary"
                    className="btn-md w-100"
                    onClick={onEmailQuoteClicked}
                  >
                    Email Quote
                  </Button>
                </Col>
                {unitAvailable ? (
                  <Col xs={6}>
                    <Button className="btn-md w-100" onClick={onBookUnitClicked}>
                      Book Unit
                    </Button>
                  </Col>
                ) : (
                  <Col xs={6}>
                    <Link to={`/contact/${location.slug}`}>
                      <Button className="btn-md w-100">
                        Call for Availability{" "}
                        <IconPhone className="ms-2 d-none d-sm-inline" />
                      </Button>
                    </Link>
                  </Col>
                )}
              </Row>
            </Container>
          </div>
          <Modal show={quoteModalOpen} onHide={() => setQuoteModalOpen(false)} centered>
            <Modal.Body>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setQuoteModalOpen(false)}
              />
              <QuoteForm />
            </Modal.Body>
          </Modal>
        </>
      ) : null}
    </>
  );
}

export default UnitListing;
